import React, { useEffect } from "react";
import styled from "styled-components";
import { FaInstagram, FaTiktok, FaGoodreads, FaAmazon } from "react-icons/fa";
import Newsletter from "../newsletter/Newsletter";
import Footer from "../privacy-policy/Footer";

const Parallax = styled.div`
  background-image: url("/images/parallax.jpg");
  background-attachment: fixed;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  padding-top: 10px;
`;

const ClipedBox = styled.div`
  background-color: rgba(255, 255, 255, 0.66);
  mask: radial-gradient(40px at 40px 40px, transparent 98%, black) -40px -40px;
  margin: clamp(15px, 5vw, 30px);
  padding: clamp(15px, 5vw, 60px);
  border-radius: clamp(15px, 3vw, 20px);
`;

const AboutContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 50px;

  @media (max-width: 1000px) {
    flex-direction: column;
    gap: 30px;
  }
`;

const Title = styled.h1`
  color: #0360ffa2;
  text-align: center;
  font-family: 'MedievalSharp', serif;
  font-size: clamp(1.5rem, 5vw, 2.8rem);
  padding-bottom: clamp(15px, 3vw, 25px);
`;

const Paragraph = styled.p`
  color: #2b60bdff;
  font-family: 'Mozilla Text', serif;
  font-size: clamp(1rem, 2vw, 1.5rem);
  line-height: 1.6;
`;

const SpecialText = styled.p`
  font-weight: bold;
  font-style: italic;
  text-align: center;
`;

const CarouselContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const Arrow = styled.button`
  height: fit-content;
  background: none;
  border: none;
  font-size: clamp(2rem, 2.5vw + 1rem, 4rem);
  font-weight: bold;
  color: #3695f4ff;
  cursor: pointer;

  &:hover {
    color: #0360ffa2;
    transform: scale(1.08);
  }
`;

const CarouselImage = styled.img`
  width: 200px;
  height: auto;
  border-radius: 10px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: rgb(38, 57, 77) 0px 20px 30px -10px;
  cursor: pointer;

  &:hover {
    transform: scale(1.02);
    box-shadow: rgb(38, 57, 77) 0px 20px 40px -10px;
  }

  @media (max-width: 1000px) {
    width: clamp(100px, 30vw, 200px);

  }
`;

const ConnectContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 50px;
  margin: clamp(15px, 5vw, 30px);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 30px;
  }
`;

const LinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: row;
  }
`;

const Link = styled.a`
  color: white;

  svg {
    width: clamp(40px, 5vw, 60px);
    height: auto;
  }

  &:hover {
    color: rgba(255, 255, 255, 0.66);
    transform: scale(1.09);
  }
`;

const bookCovers = [
  {
    src: "/images/b1.jfif",
    alt: "Beyond Prejudice by Anna Miki",
    url: "/beyond-prejudice"
  },
  {
    src: "/images/b2.jpg",
    alt: "When the Butterflies Are Gone by Anna Miki",
    url: "/when-the-butterflies-are-gone"
  }
];

const About = () => {
  useEffect(() => {
    document.title = "About - Anna Miki";
  }, []);

  const [index, setIndex] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setIndex(prev => (prev + 1) % bookCovers.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Parallax>

      <ClipedBox>
      
        <AboutContainer>

          <div>

            <Title>About the Author</Title>

            <Paragraph>
              Born and raised in Romania, Anna Miki found her way across the world to the U.S. for a time, and then back home. She writes romance, romantic suspense, and women’s fiction that dives deep into love, longing, and the twists life throws our way. Her stories are rich with high emotions and drama because, let’s face it, the best tales are the ones that make you feel everything.
            </Paragraph> <br/>

            <Paragraph>
              When she’s not dreaming up characters who love fiercely and fight for what they want, you can find her savoring life’s quieter moments—like reading on the beach, where the waves provide the perfect soundtrack to a great book. She’s married to her real-life hero, and together they’ve raised two children who have taught her everything about life and love.
            </Paragraph> <br/>

            <Paragraph>
              <SpecialText>If you’re drawn to stories that stir your soul, she’s your kindred spirit.</SpecialText>
            </Paragraph>

          </div>

          <CarouselContainer>

            <Arrow onClick={() => setIndex(prev => prev === 0 ? bookCovers.length - 1 : prev - 1)}>❮</Arrow>

            <a href={bookCovers[index].url} target="_self" rel="noopener noreferrer">
              <CarouselImage src={bookCovers[index].src} alt={bookCovers[index].alt} />
            </a>

            <Arrow onClick={() => setIndex(prev => (prev + 1) % bookCovers.length)}>❯</Arrow>
          
          </CarouselContainer>

        </AboutContainer>
        
      </ClipedBox>

      <ConnectContainer>

        <LinksContainer>

          <Link href="https://www.instagram.com/annamiki_author/" target="_blank" rel="noopener noreferrer">
            <FaInstagram />
          </Link>

          <Link href="https://www.tiktok.com/@annamiki84" target="_blank" rel="noopener noreferrer">
            <FaTiktok />
          </Link>

          <Link href="https://www.goodreads.com/author/show/51714605.Anna_Miki" target="_blank" rel="noopener noreferrer">
            <FaGoodreads />
          </Link>

          <Link href="https://www.amazon.com/stores/Anna-Miki/author/B0DKYMFFFN" target="_blank" rel="noopener noreferrer">
            <FaAmazon />
          </Link>

        </LinksContainer>

        <Newsletter/>

      </ConnectContainer>

      <Footer />

    </Parallax>
  );
};

export default About;
